import React, { useContext, useEffect, useState } from 'react';
import { Button, Card, Skeleton, Table, Tag, Tooltip, message, theme } from 'antd';
import { EyeInvisibleFilled, EyeOutlined } from '@ant-design/icons';
import type { TableColumnsType } from 'antd';
import type { Dayjs } from 'dayjs';
import { ContextStatus } from '@leedpulse-monorepo/enums';
import { formatNumber } from '@leedpulse-monorepo/utils';
import AuthenticationContext from '../../../contexts/auth/AuthenticationContext';
import getOverviewSms from '../../../../domain/use_cases/leedflow/overview/getOverviewSms';
import { formatCallNumber, renderConfidential, renderDate, renderPlatform, renderLine } from './callCells';
import type { LastSmsEntry } from '../../../../domain/use_cases/leedflow/overview/getOverviewStats';

const { useToken } = theme;

interface LastSmsTableProps {
  /**
   * Les SMS les plus récents de la période, tels que les porte la charge utile
   * de l'aperçu (`LAST_SMS_LIMIT` lignes). Ils servent les premières pages sans
   * aller-retour supplémentaire ; au-delà, la table va les chercher page par
   * page sur `/leedflow/overview-sms`. Même mécanique que `LastCallsTable`.
   */
  rows: LastSmsEntry[];
  /** SMS de la période, tous confondus — cf. `LastCallsTable`. */
  total?: number;
  status: ContextStatus;
  /** Même période et même filtre que l'aperçu, pour les pages suivantes. */
  period?: [Dayjs | null, Dayjs | null];
  subscriptionIds?: string[];
  exclude?: string[];
}

// `role` porte la direction d'un SMS comme `direction` celle d'un appel :
// `assistant` = émis par Leedflow, `user` = reçu du prospect (cf. buildSmsStats).
const renderRole = (role: string | null): React.ReactNode => {
  if (role === 'assistant') return <Tag color="success">Envoyé</Tag>;
  if (role === 'user') return <Tag color="processing">Reçu</Tag>;
  return <Tag>Inconnu</Tag>;
};

const LastSmsTable: React.FC<LastSmsTableProps> = ({ rows, total, status, period, subscriptionIds, exclude }) => {
  const { token } = useToken();
  const authContext = useContext(AuthenticationContext);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [fetched, setFetched] = useState<LastSmsEntry[]>([]);
  const [fetching, setFetching] = useState(false);
  const [filters, setFilters] = useState({ period, subscriptionIds, exclude });

  // Une page 8 n'a aucun sens sur une autre période : on revient en tête. En
  // rendu et non dans un effet — cf. `LastCallsTable`.
  if (filters.period !== period || filters.subscriptionIds !== subscriptionIds || filters.exclude !== exclude) {
    setFilters({ period, subscriptionIds, exclude });
    setPage(1);
  }

  // La charge utile de l'aperçu couvre la page demandée tant qu'on reste dans
  // ses `rows` — ou qu'elle porte déjà toute la période. Le serveur n'est
  // sollicité que pour ce qu'elle ne contient pas.
  const payloadCoversPage =
    (total !== undefined && rows.length >= total) || page * pageSize <= rows.length;

  useEffect(() => {
    if (payloadCoversPage) return;
    let cancelled = false;
    (async () => {
      setFetching(true);
      try {
        const res = await getOverviewSms(authContext.authentication, page, pageSize, period, subscriptionIds, exclude);
        if (!cancelled) setFetched(res);
      } catch (e) {
        if (!cancelled) {
          setFetched([]);
          message.error(e instanceof Error ? e.message : 'Impossible de charger cette page de SMS.');
        }
      } finally {
        if (!cancelled) setFetching(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authContext.authentication, payloadCoversPage, page, pageSize, period, subscriptionIds, exclude]);

  const pageRows = payloadCoversPage ? rows.slice((page - 1) * pageSize, page * pageSize) : fetched;

  // Masqué par défaut, révélé ligne par ligne.
  //
  // Ces messages sont la conversation privée d'un client avec son prospect :
  // les afficher en clair dans un tableau de bord interne les expose à qui
  // passe derrière l'écran, sans que personne ait eu besoin de les demander.
  // On garde la colonne — elle sert à reconnaître un SMS — mais on rend le
  // geste délibéré.
  //
  // L'état vit ici et non par ligne : la pagination démonte les lignes, un
  // état local à chacune se réinitialiserait au retour sur la page 1, ce qui
  // masquerait sans le dire ce qu'on venait d'ouvrir.
  const [revealedIds, setRevealedIds] = useState<Set<string>>(new Set());

  const toggleReveal = (id: string) =>
    setRevealedIds((previous) => {
      const next = new Set(previous);
      if (!next.delete(id)) next.add(id);
      return next;
    });

  const columns: TableColumnsType<LastSmsEntry> = [
    {
      title: 'Date',
      dataIndex: 'created',
      key: 'created',
      render: (created: number) => renderDate(created),
    },
    {
      title: 'Type',
      dataIndex: 'role',
      key: 'role',
      render: (role: string | null) => renderRole(role),
    },
    {
      title: 'Message',
      dataIndex: 'preview',
      key: 'preview',
      // La colonne qui absorbe la largeur restante : c'est elle qu'on lit.
      width: '38%',
      render: (preview: string | null, record: LastSmsEntry) => {
        // Le message d'un contact confidentiel n'est pas masqué derrière un
        // clic : il n'a pas quitté le backend. Rien à révéler, donc pas d'œil.
        if (record.confidential) return renderConfidential();
        if (!preview) return <span style={{ color: token.colorTextTertiary }}>-</span>;
        const isRevealed = revealedIds.has(record.id);
        return (
          // `overflow: hidden` porté par le conteneur et non par le texte :
          // le halo d'un `filter: blur` déborde de l'élément qui le porte, et
          // seul un ancêtre qui coupe l'empêche de baver sur la colonne
          // voisine. Un `overflow` posé sur l'élément flouté lui-même ne
          // rognerait pas son propre flou.
          <span style={{ display: 'inline-flex', alignItems: 'flex-start', gap: 6, maxWidth: '100%', overflow: 'hidden' }}>
            <Button
              type="text"
              size="small"
              icon={isRevealed ? <EyeOutlined /> : <EyeInvisibleFilled />}
              onClick={() => toggleReveal(record.id)}
              // Le libellé ne cite jamais le message : un lecteur d'écran le
              // dirait à voix haute alors qu'il est justement masqué.
              aria-label={isRevealed ? 'Masquer le message' : 'Afficher le message'}
              style={{ flexShrink: 0 }}
            />
            {isRevealed ? (
              <Tooltip title={preview}><span>{preview}</span></Tooltip>
            ) : (
              // Pas d'infobulle ici : elle rendrait le contenu au survol,
              // c'est-à-dire sans le clic qu'on vient d'exiger.
              <span
                className="blur"
                // `select: none` en plus du flou : un texte flouté reste
                // sélectionnable, donc copiable et lisible dans le presse-papier.
                style={{ userSelect: 'none' }}
                aria-hidden="true"
              >
                {preview}
              </span>
            )}
          </span>
        );
      },
    },
    // Même règle que les appels : sur un SMS reçu, c'est le contact
    // l'expéditeur et l'abonnement le destinataire.
    {
      title: 'Destinataire',
      key: 'receiver',
      render: (_: unknown, record: LastSmsEntry) =>
        record.role === 'user'
          ? renderLine(record)
          : formatCallNumber(record.customer),
    },
    {
      title: 'Expéditeur',
      key: 'emitter',
      render: (_: unknown, record: LastSmsEntry) =>
        record.role === 'user'
          ? formatCallNumber(record.customer)
          : renderLine(record),
    },
    {
      title: 'Plateforme',
      dataIndex: 'platform',
      key: 'platform',
      render: (platform: string | null) => renderPlatform(platform),
    },
    {
      title: 'Caractères',
      dataIndex: 'length',
      key: 'length',
      align: 'right',
      // Le nombre de segments explique la facture mieux que le nombre de
      // caractères seul : un SMS de 200 caractères en coûte deux.
      render: (length: number | null, record: LastSmsEntry) => (
        length === null
          ? '-'
          : (
            <span style={{ fontVariantNumeric: 'tabular-nums' }}>
              {formatNumber(length)}
              {record.segments && record.segments > 1 ? (
                <span style={{ color: token.colorTextTertiary }}> · {record.segments} seg.</span>
              ) : null}
            </span>
          )
      ),
    },
  ];

  return (
    <Card title="SMS" style={{ marginTop: token.marginLG }}>
      {status === ContextStatus.LOADING ? (
        <Skeleton active paragraph={{ rows: 6 }} />
      ) : (
        <Table
          rowKey="id"
          dataSource={pageRows}
          columns={columns}
          loading={fetching}
          // Pagination pilotée par le serveur : `dataSource` ne porte que la
          // page courante, `total` dit combien de SMS la période compte.
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            pageSizeOptions: [10, 25, 50, 100],
            showTotal: (count) => `${count} SMS sur la période`,
            onChange: (nextPage, nextPageSize) => {
              setPage(nextPageSize === pageSize ? nextPage : 1);
              setPageSize(nextPageSize);
            },
          }}
          size="small"
          scroll={{ x: true }}
        />
      )}
    </Card>
  );
};

export default LastSmsTable;
